import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, from } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../authentication/auth.service';
@Injectable()

/**
 * attaches X-XSRF-TOKEN to every backend api request
 * token used to prevent api call from postman or other related softwares
 */

export class TokenInterceptor implements HttpInterceptor {
  token: any;
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(
    private authService: AuthService
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.token = this.authService.authorizationHeaderValue();
    if (this.token) {
      const authReq = this.addAuthHeaders(req, this.token);
      return next.handle(authReq).pipe(
        catchError(error => {
          if (error instanceof HttpErrorResponse && error.status === 401) {
            return this.handle401Error(req, next);
          }
          return throwError(() => error);
        })
      );
    } else if (req.url.includes('ReadUnsubscribeUrl') || req.url.includes('UnsubscribeRecipient') ) {
      const authReq = req.clone({
        headers: req.headers.set('X-XSRF-TOKEN', this.getCookie('XSRF-TOKEN')),
        withCredentials: true
      });
      return next.handle(authReq);
    } else if (req.url.includes('GetXSRFToken') ) {
      const authReq = req.clone({
        withCredentials: true
      });
       return next.handle(authReq);
    }
    return next.handle(req);
  }

  private addAuthHeaders(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({
      headers: req.headers.set('Authorization', token).set('X-XSRF-TOKEN', this.getCookie('XSRF-TOKEN')),
      withCredentials: true
    });
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return from(this.authService.manager.signinSilent()).pipe(
        // Step 1: catch signinSilent failure — refresh token is dead, go to login, stop here
        catchError(silentRenewErr => {
          this.isRefreshing = false;
          // Unblock any queued requests so they don't hang forever
          this.refreshTokenSubject.next('');
          this.authService.login();
          return throwError(() => silentRenewErr);
        }),
        // Step 2: signinSilent succeeded — retry the original request exactly once
        switchMap(user => {
          this.isRefreshing = false;
          const newToken = `${user.token_type} ${user.access_token}`;
          this.refreshTokenSubject.next(newToken);

          return next.handle(this.addAuthHeaders(req, newToken)).pipe(
            // Step 3: if the retry STILL gets 401 — go to login, no further refresh attempt
            catchError(retryErr => {
              if (retryErr instanceof HttpErrorResponse && retryErr.status === 401) {
                this.authService.login();
              }
              return throwError(() => retryErr);
            })
          );
        })
      );
    }

    // A refresh is already in progress — wait for it, then retry once (no further refresh)
    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => {
        // Empty string means the refresh failed — propagate error to subscriber
        if (!token) {
          return throwError(() => ({ status: 401, message: 'Session expired' }));
        }
        return next.handle(this.addAuthHeaders(req, token));
      })
    );
  }
  removeCookie(sKey, sPath, sDomain) {
    document.cookie = encodeURIComponent(sKey) + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT'
      + (sDomain ? '; domain=' + sDomain : '') + (sPath ? '; path=' + sPath : '');
  }

  getCookie(cname) {
    const name = cname + '=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (const eachCA of ca) {
      let c = eachCA;
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return '';
  }
}
